import { useState } from 'react'
import Link from '@/components/ui/Link'
import { cn } from '@/lib/utils'

export default function BlogTabs({
	data,
	activeCategorySlug
}: {
	data: {categories: any[], archives: any[]}
	activeCategorySlug?: string
}) {
  const [activeTab, setActiveTab] = useState<'categories' | 'archives'>('categories')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const formatArchiveDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
  }

  return (
		<div className='BlogTabs'>
			{/* Desktop version */}
			<div className='hidden md:block'>
				<div className='flex flex-wrap justify-center gap-3'>
					<Link
						href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}`}
						className={cn(
							'rounded-[38px] px-6 py-3 outline outline-[0.50px] outline-offset-[-0.50px] outline-[#d1c1b4] flex justify-center items-center gap-2 text-xl tracking-wide transition-colors',
							!activeCategorySlug
								? 'bg-brown text-white'
								: 'text-[#69518f] hover:bg-brown hover:text-white'
						)}
					>
						View All
					</Link>
					{data.categories.map(category => (
						<Link
							key={category.id}
							href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG}/${category.slug}`}
							className={cn(
								'rounded-[38px] px-6 py-3 outline outline-[0.50px] outline-offset-[-0.50px] outline-[#d1c1b4] flex justify-center items-center gap-2 text-xl tracking-wide transition-colors',
								activeCategorySlug === category.slug
									? 'bg-brown text-white'
									: 'text-[#69518f] hover:bg-brown hover:text-white'
							)}
						>
							{category.title}
						</Link>
					))}
				</div>
			</div>

						{/* Mobile accordion version */}
			<div className='md:hidden'>
				<div className='flex flex-col gap-4'>
					{/* Categories accordion */}
					<div className='relative'>
						<div className='absolute inset-0 bg-gradient-to-br from-[#f8f6f3] to-[#f0ede8] rounded-[38px] shadow-lg'></div>
						<div className='relative'>
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className='w-full px-8 py-4 flex justify-between items-center text-[#69518f] text-xl tracking-wide font-medium hover:text-brown transition-all duration-300 group'
							>
								<span className='flex items-center gap-3'>
									<svg
										className='w-6 h-6 text-brown-light group-hover:text-brown transition-colors duration-300'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
										/>
									</svg>
									{activeCategorySlug
										? data.categories.find(
												cat => cat.slug === activeCategorySlug
										  )?.title || 'Categories'
										: `Categories (${data.categories.length})`}
								</span>
								<div className='flex items-center gap-2'>
									<span className='text-sm text-neutral-500 group-hover:text-brown transition-colors duration-300'>
										{isMobileMenuOpen ? 'Hide' : 'Show'}
									</span>
									<svg
										className={cn(
											'w-5 h-5 text-brown-light group-hover:text-brown transition-all duration-300',
											isMobileMenuOpen ? 'rotate-180' : ''
										)}
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M19 9l-7 7-7-7'
										/>
									</svg>
								</div>
							</button>
							
							{/* Accordion content */}
							<div
								className={cn(
									'overflow-hidden transition-all duration-500 ease-out',
									isMobileMenuOpen
										? 'max-h-[500px] opacity-100'
										: 'max-h-0 opacity-0'
								)}
							>
								<div className='px-8 pb-6'>
									<div className='border-t border-[#d1c1b4]/30 pt-4 space-y-1'>
										{/* View All Posts link */}
										<Link
											href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}`}
											className={cn(
												'group flex items-center gap-3 py-3 px-4 rounded-2xl text-lg tracking-wide transition-all duration-300 transform hover:translate-x-1',
												!activeCategorySlug
													? 'text-brown bg-white/80 font-semibold'
													: 'text-[#69518f] hover:text-brown hover:bg-white/60'
											)}
											onClick={() => setIsMobileMenuOpen(false)}
										>
											<div
												className={cn(
													'w-2 h-2 rounded-full transition-colors duration-300 flex-shrink-0',
													!activeCategorySlug
														? 'bg-brown'
														: 'bg-brown-light group-hover:bg-brown'
												)}
											></div>
											<span className='font-medium text-black'>View All Posts</span>
											<svg
												className='w-2 h-2 ml-auto opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1 flex-shrink-0'
												fill='none'
												stroke='currentColor'
												viewBox='0 0 24 24'
											>
												<path
													strokeLinecap='round'
													strokeLinejoin='round'
													strokeWidth={2}
													d='M9 5l7 7-7 7'
												/>
											</svg>
										</Link>
										
										{/* Categories */}
										{data.categories.map((category, index) => (
											<Link
												key={category.id}
												href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG}/${category.slug}`}
												className={cn(
													'group flex items-center gap-3 py-3 px-4 rounded-2xl text-lg tracking-wide transition-all duration-300 transform hover:translate-x-1',
													activeCategorySlug === category.slug
														? 'text-brown bg-white/80 font-semibold'
														: 'text-[#69518f] hover:text-brown hover:bg-white/60'
												)}
												onClick={() => setIsMobileMenuOpen(false)}
											>
												<div
													className={cn(
														'w-2 h-2 rounded-full transition-colors duration-300 flex-shrink-0',
														activeCategorySlug === category.slug
															? 'bg-brown'
															: 'bg-brown-light group-hover:bg-brown'
													)}
												></div>
												<span className='font-medium text-black'>{category.title}</span>
												<svg
													className='w-2 h-2 ml-auto transition-all duration-300 transform group-hover:translate-x-1 text-black hover:text-brown flex-shrink-0'
													fill='none'
													stroke='currentColor'
													viewBox='0 0 24 24'
												>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														strokeWidth={2}
														d='M9 5l7 7-7 7'
													/>
												</svg>
											</Link>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
} 